import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import {
  border,
  headerStyle,
  textStyle,
  numeroStyle,
  dateStyle,
  totalTextoStyle,
  celdaTexto,
  celdaNumero,
  celdaFecha,
} from "../../../Components/ExcelEstilos";
import { descargarLibro, limpiarNombreArchivo } from "../../../Components/ExcelTablaReporte";
import { momentDeFechaDia } from "../../../Components/fmtFechaDia";

// Libro con el mismo acomodo que la "Libreta de produccion" del taller: hoja "Resumen"
// (persona × semana) y una hoja por semana con una seccion por etapa (MONTAJE, RETOQUE...) y,
// a la derecha, el cuadro "Resumen de la semana" por persona.

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

const tituloStyle = { font: { bold: true, sz: 14, color: { rgb: "005F6B" } } };
const subtituloStyle = { font: { bold: true, sz: 11 } };
const metaStyle = { font: { sz: 9, color: { rgb: "8C8C8C" } } };
const bandaStyle = {
  font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "007A8A" } },
  alignment: { horizontal: "left", vertical: "center" },
  border,
};
const totalNumeroStyle = { ...numeroStyle, font: { bold: true }, fill: { fgColor: { rgb: "F2F2F2" } } };
const totalEtiquetaStyle = { ...totalTextoStyle, fill: { fgColor: { rgb: "F2F2F2" } } };
const transcurridosStyle = { ...numeroStyle, font: { italic: true, color: { rgb: "8C8C8C" } } };

const ESTILO_ESTATUS = {
  Terminado: { fill: { fgColor: { rgb: "E6F4EA" } }, font: { bold: true, color: { rgb: "237804" } } },
  Entregado: { fill: { fgColor: { rgb: "D6F4F8" } }, font: { bold: true, color: { rgb: "005F6B" } } },
  Pausado: { fill: { fgColor: { rgb: "FFF1D6" } }, font: { color: { rgb: "874D00" } } },
  Reasignada: { fill: { fgColor: { rgb: "F2F2F2" } }, font: { color: { rgb: "595959" } } },
};

const COLUMNAS_SEMANA = [
  { titulo: "Persona", ancho: 24 },
  { titulo: "Número", ancho: 10 },
  { titulo: "Cliente", ancho: 28 },
  { titulo: "Especie", ancho: 34 },
  { titulo: "Cantidad", ancho: 9 },
  { titulo: "Fecha inicio", ancho: 12 },
  { titulo: "Fecha término", ancho: 12 },
  { titulo: "Días hábiles", ancho: 10 },
  { titulo: "Estatus", ancho: 12 },
  { titulo: "Notas", ancho: 36 },
];
const ANCHO_IZQ = COLUMNAS_SEMANA.length; // A:J
const COL_DER = ANCHO_IZQ + 1; // L (K queda de separacion)

export const especieConDetalle = (fila) => fila.especie + (fila.detalle ? ` (${fila.detalle})` : "");

const textoDia = (fecha) => {
  const m = momentDeFechaDia(fecha);
  if (!m) return "";
  return `${DIAS[m.day()]} ${m.date()} de ${MESES[m.month()]}`;
};

export const textoSemana = (semana) => {
  const fin = momentDeFechaDia(semana.fin);
  const texto = `${textoDia(semana.inicio)} al ${textoDia(semana.fin)} de ${fin ? fin.year() : ""}`;
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const vacias = (n) => Array.from({ length: n }, () => null);

const merge = (fila, colIni, colFin) => ({ s: { r: fila, c: colIni }, e: { r: fila, c: colFin } });

// Bloque de una etapa dentro de la hoja de una semana. Regresa los renglones (con la parte
// izquierda en A:J y el cuadro resumen en L:N) y los rangos a combinar relativos al bloque.
const bloqueSeccion = (seccion) => {
  const izquierda = [];
  const merges = [];

  izquierda.push([celdaTexto(seccion.etapaNombre.toUpperCase(), bandaStyle), ...vacias(ANCHO_IZQ - 1).map(() => celdaTexto("", bandaStyle))]);
  merges.push(merge(0, 0, ANCHO_IZQ - 1));
  izquierda.push(COLUMNAS_SEMANA.map((c) => celdaTexto(c.titulo, headerStyle)));

  if (seccion.filas.length === 0) {
    izquierda.push([celdaTexto("Sin piezas en esta semana", textStyle), ...vacias(ANCHO_IZQ - 1).map(() => celdaTexto("", textStyle))]);
  }
  for (const fila of seccion.filas) {
    const estiloEstatus = ESTILO_ESTATUS[fila.estatusTexto];
    izquierda.push([
      celdaTexto(fila.empleadoNombre),
      celdaTexto(fila.numero),
      celdaTexto(fila.clienteNombre),
      celdaTexto(especieConDetalle(fila)),
      celdaNumero(fila.cantidad),
      celdaFecha(momentDeFechaDia(fila.fechaInicio)),
      fila.fechaTermino ? celdaFecha(momentDeFechaDia(fila.fechaTermino)) : celdaTexto("", dateStyle),
      celdaNumero(fila.diasHabiles, fila.diasTranscurridos ? transcurridosStyle : numeroStyle),
      celdaTexto(fila.estatusTexto, estiloEstatus ? { ...textStyle, ...estiloEstatus } : textStyle),
      celdaTexto(fila.notas),
    ]);
  }
  izquierda.push([
    ...vacias(3),
    celdaTexto("Total animales", totalEtiquetaStyle),
    celdaNumero(seccion.totalAnimales, totalNumeroStyle),
  ]);

  const derecha = [
    [celdaTexto("RESUMEN DE LA SEMANA", bandaStyle), celdaTexto("", bandaStyle), celdaTexto("", bandaStyle)],
    [celdaTexto(seccion.etapaNombre, headerStyle), celdaTexto("Animales", headerStyle), celdaTexto("Terminados", headerStyle)],
    ...seccion.resumen.map((p) => [celdaTexto(p.nombre), celdaNumero(p.animales), celdaNumero(p.terminados)]),
    [celdaTexto("Total", totalEtiquetaStyle), celdaNumero(seccion.totalAnimales, totalNumeroStyle), celdaNumero(seccion.totalTerminados, totalNumeroStyle)],
  ];
  merges.push(merge(0, COL_DER, COL_DER + 2));

  const renglones = Array.from({ length: Math.max(izquierda.length, derecha.length) }, (_, i) => {
    const izq = izquierda[i] || [];
    return [...izq, ...vacias(ANCHO_IZQ - izq.length), null, ...(derecha[i] || [])];
  });

  return { renglones, merges };
};

const hojaSemana = (semana, empresaNombre) => {
  const aoa = [
    [celdaTexto("Control de producción semanal", tituloStyle)],
    [celdaTexto(textoSemana(semana), subtituloStyle)],
    [celdaTexto(empresaNombre || "", metaStyle)],
    [],
  ];
  const merges = [];

  for (const seccion of semana.secciones) {
    const inicio = aoa.length;
    const { renglones, merges: mergesBloque } = bloqueSeccion(seccion);
    aoa.push(...renglones, []);
    mergesBloque.forEach((m) => merges.push({ s: { r: m.s.r + inicio, c: m.s.c }, e: { r: m.e.r + inicio, c: m.e.c } }));
  }

  aoa.push([celdaTexto("Días hábiles en gris: transcurridos al cierre de la semana (la pieza seguía en proceso).", metaStyle)]);

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = [...COLUMNAS_SEMANA.map((c) => ({ wch: c.ancho })), { wch: 3 }, { wch: 24 }, { wch: 11 }, { wch: 12 }];
  hoja["!merges"] = merges;
  return hoja;
};

// Matriz persona × semana de una etapa: `valor(p, i)` da la celda de la semana i y
// `total(p)` la ultima columna.
const matrizResumen = ({ titulo, semanas, personas, valor, tituloTotal, total }) => {
  const nCols = semanas.length + 2;
  const renglones = [
    [celdaTexto(titulo, bandaStyle), ...vacias(nCols - 1).map(() => celdaTexto("", bandaStyle))],
    [celdaTexto("Persona", headerStyle), ...semanas.map((s) => celdaTexto(s.etiqueta, headerStyle)), celdaTexto(tituloTotal, headerStyle)],
    ...personas.map((p) => [celdaTexto(p.nombre), ...semanas.map((_, i) => celdaNumero(valor(p, i))), celdaNumero(total(p))]),
    [
      celdaTexto("Total", totalEtiquetaStyle),
      ...semanas.map((_, i) => celdaNumero(personas.reduce((s, p) => s + valor(p, i), 0), totalNumeroStyle)),
      celdaNumero(personas.reduce((s, p) => s + total(p), 0), totalNumeroStyle),
    ],
  ];
  return { renglones, merges: [merge(0, 0, nCols - 1)] };
};

const hojaResumen = (datos, empresaNombre, descripcionFiltros) => {
  const aoa = [
    [celdaTexto("Resumen de animales por persona y semana", tituloStyle)],
    [celdaTexto(empresaNombre || "", subtituloStyle)],
    [celdaTexto(descripcionFiltros || "", metaStyle)],
    [celdaTexto(`Generado ${moment().format("DD/MM/YYYY HH:mm")}`, metaStyle)],
    [],
  ];
  const merges = [];
  const agregar = ({ renglones, merges: m }) => {
    const inicio = aoa.length;
    aoa.push(...renglones, []);
    m.forEach((x) => merges.push({ s: { r: x.s.r + inicio, c: x.s.c }, e: { r: x.e.r + inicio, c: x.e.c } }));
  };

  for (const etapa of datos.resumen) {
    agregar(
      matrizResumen({
        titulo: `${etapa.etapaNombre.toUpperCase()} — Animales trabajados`,
        semanas: datos.semanas,
        personas: etapa.personas,
        valor: (p, i) => p.porSemana[i]?.animales || 0,
        tituloTotal: "Piezas distintas",
        total: (p) => p.totalPiezas,
      })
    );
    agregar(
      matrizResumen({
        titulo: `${etapa.etapaNombre.toUpperCase()} — Terminados`,
        semanas: datos.semanas,
        personas: etapa.personas,
        valor: (p, i) => p.porSemana[i]?.terminados || 0,
        tituloTotal: "Total",
        total: (p) => p.totalTerminados,
      })
    );
  }

  aoa.push([
    celdaTexto(
      "Una pieza que no se termina en la semana aparece también en la siguiente: por eso 'Piezas distintas' no es la suma de las semanas.",
      metaStyle
    ),
  ]);

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = [{ wch: 26 }, ...datos.semanas.map(() => ({ wch: 13 })), { wch: 15 }];
  hoja["!merges"] = merges;
  return hoja;
};

export const descargarExcelProduccion = ({ datos, empresaNombre, descripcionFiltros, desde, hasta }) => {
  const hojas = [
    { nombre: "Resumen", hoja: hojaResumen(datos, empresaNombre, descripcionFiltros) },
    ...datos.semanas.map((semana) => ({ nombre: semana.etiqueta, hoja: hojaSemana(semana, empresaNombre) })),
  ];
  descargarLibro(hojas, limpiarNombreArchivo(`Produccion_Taller_${desde}_${hasta}`));
};
